import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Trash2, RefreshCw } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { useUser } from '@/shared/hooks/useAuth';
import { useJobs, useJobMutations } from '../hooks/useJobs';
import { JOB_STATUS_LABELS, JOB_STATUS_TONES } from '../constants';
import type { Job } from '../types';

function fmt(iso?: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function JobHistory() {
  const user = useUser();
  const roleLower = String(user?.role ?? '').toLowerCase();
  const isAdmin = ['admin', 'superadmin', 'owner'].includes(roleLower);
  const { data: jobs = [], isLoading, isError, isFetching, refetch } = useJobs();
  const { deleteJob } = useJobMutations();
  const [confirm, setConfirm] = useState<Job | null>(null);

  const handleDelete = () => {
    if (!confirm) return;
    deleteJob.mutate(confirm.id, {
      onSuccess: () => {
        toast.success('Job deleted.');
        setConfirm(null);
      },
      onError: () => toast.error('Failed to delete job.'),
    });
  };

  const columns: Column<Job>[] = [
    { key: 'niche', header: 'Niche', render: (j) => <span className="font-medium text-on-surface">{j.niche || '—'}</span> },
    { key: 'location', header: 'Location', render: (j) => j.location || '—' },
    ...(isAdmin ? [{ key: 'owner', header: 'Owner', render: (j: Job) => j.owner_name || '—' }] : []),
    { key: 'status', header: 'Status', render: (j) => <Badge tone={JOB_STATUS_TONES[j.status] ?? 'neutral'}>{JOB_STATUS_LABELS[j.status] ?? j.status}</Badge> },
    { key: 'leads_found', header: 'Leads', render: (j) => <div className="text-right">{j.leads_found}</div> },
    { key: 'duplicates_skipped', header: 'Dupes', render: (j) => <div className="text-right">{j.duplicates_skipped}</div> },
    { key: 'started_at', header: 'Started', render: (j) => fmt(j.started_at) },
    {
      key: 'actions',
      header: '',
      render: (j) => (
        <Button
          variant="ghost"
          size="sm"
          className="text-danger hover:bg-danger/10"
          onClick={(e) => {
            e.stopPropagation();
            setConfirm(j);
          }}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <span className="font-semibold text-on-surface">Job History</span>
          <Button variant="ghost" size="sm" onClick={() => refetch()} loading={isFetching}>
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : isError ? (
          <EmptyState title="Failed to load jobs" description="Check your connection or try refreshing." />
        ) : jobs.length === 0 ? (
          <EmptyState title="No scans yet" description="Your scrape history will appear here." />
        ) : (
          <Table bare columns={columns} data={jobs} rowKey={(j) => j.id} empty="No scans yet." />
        )}
      </CardBody>

      <ConfirmDialog
        open={!!confirm}
        title="Delete job"
        message={`Delete the "${confirm?.niche ?? ''}" scan record? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteJob.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />
    </Card>
  );
}